import React, { useEffect, useState } from 'react'
import { hapticTick } from '../../../../../../_utils/haptics'
import { useLiveCodes, findById, getState } from '../../../../../discounts/discountStore'
import { headlineOf, offerWords, stateOf, dateSaid, STATE_LABEL } from '../../../../../discounts/data/codes'

// An Offer post's code: one of the salon's live codes (Marketing ›
// Discounts), never typed, so a post can't advertise a code that doesn't
// exist. Picking one fills in what it's worth, what it covers, when, the
// code on the design and its small print. A code that has since ended or
// been paused is called out before the post goes anywhere.
const blank = { code: '', codeId: '', small: '' }

const CodePicker = ({ post, onContents }) => {
  const { codes, loaded } = useLiveCodes()
  const c = post.content || {}
  const current = c.codeId ? (findById(c.codeId) || (getState().codes || []).find(x => x.code === c.code)) : null
  const [open, setOpen] = useState(!c.code)
  useEffect(() => { if (c.code) setOpen(false) }, [c.code])
  const state = current ? stateOf(current) : null
  const stale = !!c.code && loaded && (!current || !['live', 'scheduled'].includes(state))

  const pick = (code) => {
    hapticTick()
    // The design's example price goes; a price the salon typed stays
    const typedPrice = post.touched && post.touched.price && String(c.price || '').trim()
    onContents({ ...offerWords(code), ...(typedPrice ? {} : { price: '' }) }, 'code')
    setOpen(false)
  }

  if (c.code && !open) {
    return (
      <section className={`sx-cp-current${stale ? ' is-stale' : ''}`} aria-label="The code on this post">
        <div className="sx-cp-current-text">
          <span className="sx-cp-code">{c.code}</span>
          <span className="sx-cp-what">{current ? headlineOf(current) : c.value}</span>
          {stale
            ? <span className="sx-cp-warn" role="alert">{current ? `${c.code} is ${STATE_LABEL[state].toLowerCase()}, so clients can’t use it. Pick a live code or take it off before you post this.` : `${c.code} isn’t one of your codes any more. Pick a live code or take it off before you post this.`}</span>
            : <span className="sx-rp-note">{current && current.endsOn ? `Live until ${dateSaid(current.endsOn)}. ` : ''}Its small print is on the post and in the caption.</span>}
        </div>
        <div className="sx-cp-actions">
          <button type="button" className="sx-btn" onClick={() => { hapticTick(); setOpen(true) }}>Choose another</button>
          <button type="button" className="sx-link" onClick={() => { hapticTick(); onContents(blank, 'code') }}>Take the code off</button>
        </div>
      </section>
    )
  }

  return (
    <section className="sx-rp" aria-label="Add a discount code">
      <div className="sx-rp-head">
        <h3 className="sx-group-title">Add a discount code</h3>
        <p className="sx-rp-line">Pick one of your live codes and it fills in the offer, the dates and the small print.</p>
      </div>
      {!loaded && (
        <div className="sx-rp-list" aria-hidden>
          {[0, 1].map(i => <span key={i} className="sx-rp-row is-loading"><span className="sx-line" /><span className="sx-line is-short" /></span>)}
        </div>
      )}
      {loaded && !codes.length && (
        <p className="sx-empty">No live codes yet. Make one in Marketing, Discounts, and it shows here.</p>
      )}
      {loaded && codes.length > 0 && (
        <div className="sx-rp-list">
          {codes.map((code) => {
            const on = code.id === c.codeId
            return (
              <button key={code.id} type="button" className={`sx-rp-row sx-cp-row${on ? ' is-on' : ''}`} aria-pressed={on} onClick={() => pick(code)}>
                <span className="sx-rp-row-top">
                  <span className="sx-cp-code">{code.code}</span>
                  {on ? <span className="sx-rp-tag is-on">In this post</span> : stateOf(code) === 'scheduled' ? <span className="sx-rp-tag">Starts {dateSaid(code.startsOn)}</span> : null}
                </span>
                <span className="sx-cp-what">{headlineOf(code)}</span>
                <span className="sx-review-when">{code.endsOn ? `Book by ${dateSaid(code.endsOn)}` : 'No end date'}{code.audience === 'new' ? ' · New clients' : ''}</span>
              </button>
            )
          })}
        </div>
      )}
      {c.code && <button type="button" className="sx-link" onClick={() => setOpen(false)}>Keep {c.code}</button>}
    </section>
  )
}

export default CodePicker
